<template>
  <b-modal
    id="modal-webhook-add"
    ref="modal"
    v-model="open"
    size="lg"
    no-close-on-backdrop
    hide-footer
    title="ADD NEW WEBHOOK"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="addWebhookForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onClickAddWebhook)"
        >
          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Name"
                label-for="webhook-name"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="name"
                  rules="required"
                >
                  <b-form-input
                    id="webhook-name"
                    v-model="form.name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Enter webhook name"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="URL"
                label-for="webhook-url"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="url"
                  rules="required"
                >
                  <b-form-input
                    id="webhook-url"
                    v-model="form.url"
                    :state="errors.length > 0 ? false : null"
                    placeholder="https://example.com/webhook"
                    type="text"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Request Type"
                label-for="webhook-request-type"
              >
                <v-select
                  id="webhook-request-type"
                  v-model="form.request_type"
                  :options="requestTypeOptions"
                  :reduce="option => option.value"
                  label="text"
                  placeholder="Select request type"
                  autocomplete="nope"
                >
                  <template v-slot:option="option">
                    {{ option.text }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Event Type"
                label-for="webhook-event-type"
              >
                <v-select
                  id="webhook-event-type"
                  v-model="form.event_type"
                  :options="eventTypeOptions"
                  :reduce="option => option.value"
                  label="text"
                  placeholder="Select event type"
                  autocomplete="nope"
                >
                  <template v-slot:option="option">
                    {{ option.text }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="6">
              <b-form-group
                label="Max Retries"
                label-for="webhook-max-retries"
              >
                <ValidationProvider
                  #default="{ errors }"
                  name="maxRetries"
                  rules="required|min_value:1|max_value:10"
                >
                  <b-form-input
                    id="webhook-max-retries"
                    v-model="form.max_retries"
                    :state="errors.length > 0 ? false : null"
                    type="number"
                    min="1"
                    max="10"
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group label-for="webhook-merchant-business">
                <template #label>
                  Merchant Business <span class="text-danger">*</span>
                </template>
                <ValidationProvider
                  #default="{ errors }"
                  name="merchantBusiness"
                  rules="required"
                >
                  <v-select
                    id="webhook-merchant-business"
                    v-model="form.merchant_business_ids"
                    :options="businessOptions"
                    :reduce="option => option.id"
                    label="business_name"
                    placeholder="Select merchant business"
                    multiple
                    autocomplete="nope"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </ValidationProvider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-form-checkbox
                v-model="form.is_active"
                :value="true"
                :unchecked-value="false"
                style="margin-top:27px"
              >
                Active
              </b-form-checkbox>
            </b-col>
          </b-row>

          <!-- Headers Section -->
          <b-row class="mt-3">
            <b-col cols="12">
              <h6>Headers</h6>
              <div
                v-for="(header, index) in form.headers"
                :key="index"
                class="d-flex mb-2"
              >
                <div class="col-5 pr-1">
                  <b-form-input
                    v-model="header.key"
                    placeholder="Header Key"
                  />
                </div>
                <div class="col-5 px-1">
                  <b-form-input
                    v-model="header.value"
                    placeholder="Header Value"
                  />
                </div>
                <div class="col-2 pl-1 d-flex align-items-center">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="removeHeader(index)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </div>
              </div>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="addHeader"
              >
                <feather-icon icon="PlusIcon" />
                Add Header
              </b-button>
            </b-col>
          </b-row>

          <!-- Field Mappings Section -->
          <b-row class="mt-3">
            <b-col cols="12">
              <h6>Field Mappings</h6>
              <div
                v-for="(mapping, index) in form.field_mappings"
                :key="index"
                class="d-flex mb-2"
              >
                <div class="col-5 pr-1">
                  <b-form-input
                    v-model="mapping.external_field"
                    placeholder="External Field"
                  />
                </div>
                <div class="col-5 px-1">
                  <v-select
                    v-model="mapping.internal_field"
                    :options="internalFieldOptions"
                    :reduce="option => option.value"
                    label="text"
                    placeholder="Select internal field"
                  >
                    <template v-slot:option="option">
                      {{ option.text }}
                    </template>
                  </v-select>
                </div>
                <div class="col-2 pl-1 d-flex align-items-center">
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="removeFieldMapping(index)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </div>
              </div>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="addFieldMapping"
              >
                <feather-icon icon="PlusIcon" />
                Add Field Mapping
              </b-button>
            </b-col>
          </b-row>

          <!-- submit and reset -->
          <b-row>
            <b-col offset-md="4">
              <div v-can="'create-webhook'">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  class="mr-1 custom-button-color"
                  :disabled="modalLoading"
                  @click="onClickAddWebhook"
                >
                  Submit
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="reset"
                  variant="outline-secondary"
                >
                  Clear
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BRow,
  BOverlay,
  BFormCheckbox,
  VBModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const WebhookLogsRepository = RepositoryFactory.get('webhookLogs')
const MerchantRepository = RepositoryFactory.get('merchant')
function initialState() {
  return {
    open: false,
    form: {
      merchant_business_ids: [],
      name: '',
      url: '',
      request_type: 'POST',
      event_type: '',
      is_active: true,
      max_retries: 3,
      headers: [],
      field_mappings: [],
    },
    modalLoading: false,
    businessOptions: [],
    internalFieldOptions: [],
    eventTypeOptions: [],
    requestTypeOptions: [
      { text: 'POST', value: 'POST' },
      { text: 'GET', value: 'GET' },
      { text: 'PUT', value: 'PUT' },
      { text: 'PATCH', value: 'PATCH' },
    ],
  }
}
export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BRow,
    BOverlay,
    BFormCheckbox,
    ValidationObserver,
    ValidationProvider,
    'v-select': vSelect,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  methods: {
    async openModal() {
      await Object.assign(this.$data, initialState())
      this.open = true
      await this.getBusinessList()
      await this.fetchWebhookConfig()
    },
    async getBusinessList() {
      try {
        const { data } = (await MerchantRepository.getMerchantBusinessListNoPagination()).data
        this.businessOptions = data
        if (this.businessOptions.length === 1) {
          this.form.merchant_business_ids = [this.businessOptions[0].id]
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async fetchWebhookConfig() {
      try {
        const { data } = await WebhookLogsRepository.getWebhookConfig()
        // Convert payload_fields to internal field options
        this.internalFieldOptions = Object.entries(data.data.payload_fields).map(([key, value]) => ({
          text: value,
          value: key,
        }))
        // Convert event_types to event type options
        this.eventTypeOptions = Object.entries(data.data.event_types).map(([key, value]) => ({
          text: value,
          value: key,
        }))
      } catch (e) {
        // Fallback to default options if API fails
        this.internalFieldOptions = [
          { text: 'Waybill Number', value: 'waybill_number' },
          { text: 'Order Number', value: 'order_number' },
          { text: 'Status Name', value: 'status_name' },
          { text: 'Order Created Date', value: 'order_created_at' },
          { text: 'Order Updated Date', value: 'order_updated_at' },
        ]
        this.eventTypeOptions = [
          { text: 'Order Status Update', value: 'order_status_update' },
        ]
      }
    },
    addHeader() {
      this.form.headers.push({ key: '', value: '' })
    },
    removeHeader(index) {
      this.form.headers.splice(index, 1)
    },
    addFieldMapping() {
      this.form.field_mappings.push({ external_field: '', internal_field: '' })
    },
    removeFieldMapping(index) {
      this.form.field_mappings.splice(index, 1)
    },
    setSelectedFieldMappingIndex(index) {
      this.selectedFieldMappingIndex = index
    },
    addInternalFieldTag(fieldValue) {
      if (this.form.field_mappings.length === 0) {
        this.addFieldMapping()
      }
      const targetMapping = this.form.field_mappings[this.selectedFieldMappingIndex]
      if (targetMapping) {
        targetMapping.internal_field = fieldValue
      }
    },
    async onClickAddWebhook() {
      // Manual validation for all required fields
      if (!this.form.merchant_business_ids || this.form.merchant_business_ids.length === 0) {
        this.showErrorMessage('Please select a merchant business')
        return
      }
      if (!this.form.name) {
        this.showErrorMessage('Please enter a webhook name')
        return
      }
      if (!this.form.url) {
        this.showErrorMessage('Please enter a webhook URL')
        return
      }
      if (!this.form.request_type) {
        this.showErrorMessage('Please select a request type')
        return
      }
      if (!this.form.event_type) {
        this.showErrorMessage('Please select an event type')
        return
      }
      this.modalLoading = true
      try {
        const res = await WebhookLogsRepository.addWebhook(this.form)
        if (res.status === 200 || res.status === 201) {
          this.showSuccessMessage('Webhook created successfully')
          this.open = false
          this.$parent.onClickWebhookRefresh()
        } else {
          this.showErrorMessage('Failed to create webhook')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.modalLoading = false
    },
  },
}
</script>
<style scoped>
@import "../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-inputs/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";
@import '../../@core/scss/button-color.scss';
</style>
